import { createAuth0, useAuth0 as _useAuth0 } from '@auth0/auth0-vue';
import { buildAuth0Options, isLocalAuthMode } from './auth0-config';

// Initialise the real Auth0 SDK in every mode. In local mode it is pointed at
// localauth0 (`VUE_APP_AUTH0_DOMAIN=localhost:3001`) via .env.local rather than
// stubbed out, so the browser login + JWT flow is exercised for real against the
// local OIDC issuer. See src/utils/auth0-config.js and local/README.md.
const options = buildAuth0Options();

if (isLocalAuthMode()) {
  console.log('[Auth0] Local mode — pointing SDK at localauth0:', options.domain);
}
console.log('redirect_uri: ' + options.authorizationParams.redirect_uri);

const auth0 = createAuth0(options);

export { auth0 };

/**
 * Wrapper for the useAuth0 composable. Kept so callers import from
 * '@/utils/auth0' rather than '@auth0/auth0-vue' directly, giving us a single
 * seam to wrap/instrument SDK access.
 */
export function useAuth0() {
  return _useAuth0();
}

/**
 * Wrapper for getAccessTokenSilently with monitoring/logging
 * Tracks token retrieval performance and cache hits to help identify rate limit issues
 * @param {Object} options - Options for getAccessTokenSilently
 * @param {string} options.cacheMode - Cache mode: only 'on' | 'off' | 'cache-only' are valid per @auth0/auth0-spa-js. Unknown values fall back to 'on'.
 * @param {string} source - Source identifier for logging (e.g., 'router-guard', 'login')
 * @returns {Promise<string>} Access token
 */
export async function getAccessTokenSilentlyWithMonitoring(options = {}, source = 'unknown') {
  const startTime = Date.now();
  const cacheMode = options.cacheMode || 'on';

  try {
    const token = await auth0.getAccessTokenSilently(options);
    const duration = Date.now() - startTime;

    // Quick duration check: cache hits are typically < 50ms, network calls are > 100ms
    const likelyFromCache = duration < 50;

    if (process.env.VUE_APP_NODE_ENV !== 'production') {
      console.log(`[Auth0 Token] Retrieved from ${source}`, {
        cacheMode,
        duration: `${duration}ms`,
        likelyFromCache,
        timestamp: new Date().toISOString(),
      });
    }

    return token;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[Auth0 Token] Failed to retrieve from ${source}`, {
      cacheMode,
      duration: `${duration}ms`,
      error: error?.error || error?.message,
      errorCode: error?.error,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
}
